import React, { useState, useEffect } from 'react'
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaMapMarkerAlt,
  FaToggleOn,
  FaToggleOff,
} from 'react-icons/fa'
import locationService from '../services/locationService'
import Button from './ui/Button'
import Loader from './Loader'
import { toast } from 'react-toastify'

const EMPTY_FORM = {
  name: '',
  parent_id: '',
  description: '',
  image_url: '',
  price_on_request: false,
  is_active: true,
  sort_order: 0,
}

const LocationManager = () => {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [expandedRegions, setExpandedRegions] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    fetchLocations()
  }, [])

  const fetchLocations = async () => {
    setLoading(true)
    try {
      const data = await locationService.getAllLocations()
      const locs = data.locations || data || []
      setLocations(locs)
      // Auto-expand all regions
      const expanded = {}
      locs.forEach((loc) => {
        if (!loc.parent_id) expanded[loc.id] = true
      })
      setExpandedRegions(expanded)
    } catch (err) {
      toast.error('Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  const getRegions = () => locations.filter((loc) => !loc.parent_id && (!loc.children || true))

  // The API returns hierarchical data: regions with nested children arrays
  const getFilteredLocations = () => {
    if (!searchTerm) return locations
    const term = searchTerm.toLowerCase()
    return locations.filter((region) => {
      const regionMatch = region.name.toLowerCase().includes(term)
      const childMatch = region.children?.some((child) => child.name.toLowerCase().includes(term))
      return regionMatch || childMatch
    })
  }

  const handleCreate = () => {
    setEditingLocation(null)
    setForm(EMPTY_FORM)
    setShowModal(true)
  }

  const handleEdit = (location) => {
    setEditingLocation(location)
    setForm({
      name: location.name || '',
      parent_id: location.parent_id || '',
      description: location.description || '',
      image_url: location.image_url || '',
      price_on_request: !!location.price_on_request,
      is_active: location.is_active !== false,
      sort_order: location.sort_order || 0,
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Location name is required')
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...form,
        parent_id: form.parent_id || null,
        sort_order: parseInt(form.sort_order, 10) || 0,
      }
      if (editingLocation) {
        await locationService.updateLocation(editingLocation.id, payload)
        toast.success('Location updated successfully')
      } else {
        await locationService.createLocation(payload)
        toast.success('Location created successfully')
      }
      setShowModal(false)
      fetchLocations()
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save location'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (location) => {
    try {
      await locationService.deleteLocation(location.id)
      toast.success('Location deleted successfully')
      setDeleteConfirm(null)
      fetchLocations()
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete location'
      toast.error(msg)
    }
  }

  const toggleRegion = (regionId) => {
    setExpandedRegions((prev) => ({ ...prev, [regionId]: !prev[regionId] }))
  }

  const filteredLocations = getFilteredLocations()

  // Get top-level regions for the parent dropdown (excluding the location being edited)
  const regionOptions = locations.filter((loc) => !loc.parent_id && loc.id !== editingLocation?.id)

  if (loading) return <Loader />

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex-1 mr-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field !pl-10"
            />
          </div>
        </div>
        <Button icon={<FaPlus />} onClick={handleCreate}>
          Add Location
        </Button>
      </div>

      {/* Empty State */}
      {filteredLocations.length === 0 && (
        <div className="card p-12 text-center">
          <FaMapMarkerAlt className="text-4xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No locations found</p>
          <p className="text-gray-400 text-sm mt-1">
            {searchTerm
              ? 'Try a different search term'
              : 'Click "Add Location" to create your first location'}
          </p>
        </div>
      )}

      {/* Hierarchical Location Table */}
      {filteredLocations.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price on Request
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLocations.map((region) => (
                <React.Fragment key={region.id}>
                  {/* Region Row */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {region.children?.length > 0 ? (
                          <button
                            onClick={() => toggleRegion(region.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {expandedRegions[region.id] ? <FaChevronDown /> : <FaChevronRight />}
                          </button>
                        ) : (
                          <span className="w-4" />
                        )}
                        <FaMapMarkerAlt className="text-primary-500" />
                        <span className="font-semibold text-gray-900">{region.name}</span>
                        {region.children?.length > 0 && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({region.children.length} places)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${region.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                      >
                        {region.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {region.price_on_request ? (
                        <FaToggleOn className="text-amber-500 text-xl" />
                      ) : (
                        <FaToggleOff className="text-gray-300 text-xl" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{region.sort_order || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(region)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(region)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Child Places */}
                  {expandedRegions[region.id] &&
                    region.children?.map((place) => (
                      <tr key={place.id} className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2 pl-10">
                            <span className="text-gray-400">└</span>
                            <span className="text-gray-700">{place.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${place.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                          >
                            {place.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          {place.price_on_request ? (
                            <FaToggleOn className="text-amber-500 text-xl" />
                          ) : (
                            <FaToggleOff className="text-gray-300 text-xl" />
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500">{place.sort_order || 0}</td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(place)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(place)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="label">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. Manali, Uttarakhand"
                />
              </div>

              {/* Parent (Region) */}
              <div>
                <label className="label">Parent Region</label>
                <select
                  value={form.parent_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, parent_id: e.target.value }))}
                  className="input-field"
                >
                  <option value="">None (this is a region)</option>
                  {regionOptions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty to create a top-level region. Select a region to create a place within
                  it.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="label">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="input-field"
                  rows={3}
                  placeholder="Brief description of this location"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="label">Image URL</label>
                <input
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setForm((prev) => ({ ...prev, image_url: e.target.value }))}
                  className="input-field"
                  placeholder="https://example.com/image.jpg"
                />
                {form.image_url && (
                  <img
                    src={form.image_url}
                    alt="Preview"
                    className="mt-2 h-24 w-full object-cover rounded"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                )}
              </div>

              {/* Sort Order */}
              <div>
                <label className="label">Sort Order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
                  className="input-field"
                  min={0}
                />
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.price_on_request}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, price_on_request: e.target.checked }))
                    }
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Price on Request</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editingLocation ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Location</h3>
            <p className="text-gray-600 mb-1">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
            </p>
            {deleteConfirm.children?.length > 0 && (
              <p className="text-amber-600 text-sm mb-4">
                ⚠ This region has {deleteConfirm.children.length} places. They will lose their
                parent region.
              </p>
            )}
            <p className="text-gray-500 text-sm mb-4">
              Tours linked to this location will have their location reference removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LocationManager
